/**
 * The paywall's view of the person who just finished the quiz.
 *
 * Everything on these screens is the user's own result. The numbers are
 * computed with `mergeTrackResults` — the same function the post-purchase
 * dashboard uses — so the score shown for $0 on the paywall is the score they
 * find inside after paying. A second scoring path here would eventually drift
 * and make the funnel look like a bait.
 *
 * Copy for a pattern (`shows`, `res`, one-liners) comes from the Notion-built
 * content payload, already in the active language. Nothing is authored twice.
 */
import React from 'react';
import { QuizPage, UserProfile } from '../types/quiz';
import { mergeTrackResults } from '../utils/scoringV3';
import { AppContent, Pattern, useAppContent } from '../app/content';
import { Polarity, effectiveStrength, scoreColor } from '../app/tokens';

export interface PaywallPattern {
    code: string;
    name: string;
    /** One-line description, from the content payload. */
    sub: string;
    pol: Polarity;
    /** 0–100 strength, as measured. */
    pct: number;
    /** How it shows up day to day. Empty when the payload has none. */
    shows: string[];
    /** What changes when it weakens. Empty when the payload has none. */
    res: string[];
}

export interface PaywallProfile {
    ready: boolean;
    /** The direction the person was measured on. Null while loading. */
    dirCode: string | null;
    dirName: string;
    dirSub: string;
    /** The measured index, 0–100. One direction is taken pre-purchase, so this is its score. */
    index: number | null;
    /** Patterns found in that direction, most in need of work first. */
    patterns: PaywallPattern[];
    /** The strongest blocker — the one the profile and paywall both talk about. */
    blocker: PaywallPattern | null;
    /** Size of the full catalogue, for "3 of 53". */
    patternTotal: number;
    /** Directions in catalogue order, with the taken one scored and the rest locked. */
    directions: Array<{ code: string; name: string; score: number | null }>;
    /** Modelled four-week trajectory. Always rendered with "a model, not a promise". */
    projection: { now: number; goal: number; weeks: number[] } | null;
}

const EMPTY: PaywallProfile = {
    ready: false,
    dirCode: null,
    dirName: '',
    dirSub: '',
    index: null,
    patterns: [],
    blocker: null,
    patternTotal: 0,
    directions: [],
    projection: null,
};

/**
 * Modelled improvement over four weeks, from the handoff's trajectory (44 → 78).
 *
 * Kept as a gain rather than a target so a person who starts at 60 is not shown
 * a "projection" below where they already are, and capped so it never promises
 * a perfect score.
 *
 * `MIN_HEADROOM` is what stops the cap from producing nonsense at the top of
 * the scale: someone who already scores 90 has 2 points of room, and a
 * before/after that reads "90 → 92 in four weeks" is not a reason to buy
 * anything. Below that threshold there is no projection at all and the screens
 * that would draw one leave the space empty instead.
 */
const GAIN = 34;
const CEILING = 92;
const MIN_HEADROOM = 6;
const WEEKS = 4;

/** The four weekly waypoints between `now` and `goal`, eased slightly early. */
function trajectory(now: number, goal: number): number[] {
    const span = goal - now;

    return Array.from({ length: WEEKS }, (_, i) => {
        const t = (i + 1) / WEEKS;

        return Math.round(now + span * (1 - (1 - t) ** 1.6));
    });
}

function toPattern(p: Pattern, pct: number): PaywallPattern {
    return {
        code: p.code,
        name: p.name,
        sub: p.sub,
        pol: p.pol,
        pct,
        shows: p.shows || [],
        res: p.res || [],
    };
}

/**
 * Score the run that is still in progress.
 *
 * `pages` must be the pages the user was actually shown (shell + the one track
 * they took) — `computeV3Ranges` scopes headroom to exactly those, so passing a
 * fuller set would invent questions and drag every percent toward the middle.
 */
export function computePaywallProfile(profile: UserProfile, pages: QuizPage[], content: AppContent): PaywallProfile {
    const track = (profile.selected_track as string) || (profile.recommended_track as string) || '';

    if (!track) return EMPTY;

    const tagTypes: Record<string, Polarity> = {};
    const tagDimension: Record<string, string> = {};

    content.patterns.forEach((p) => {
        tagTypes[p.code] = p.pol;
        tagDimension[p.code] = p.dim;
    });

    const merged = mergeTrackResults([{ track, answers: profile, pages }], tagTypes, tagDimension);
    const dim = merged.scores.dimensions[track];

    // No measurable answers yet (a deep link, a half-finished draft). Better an
    // honest empty state than a fabricated zero.
    if (!dim) return EMPTY;

    const index = Math.round(dim.percent);
    const dir = content.dirBy[track];

    const patterns = merged.scores.tags
        .filter((t) => tagDimension[t.id] === track && content.patBy[t.id])
        .map((t) => toPattern(content.patBy[t.id], Math.round(t.percent)))
        // "Most in need of work first" — engine-inverted, so a weak engine and a
        // strong blocker both rise to the top and agree with their own colours.
        .sort((a, b) => effectiveStrength(b.pct, b.pol) - effectiveStrength(a.pct, a.pol));

    const blocker = patterns.filter((p) => p.pol === 'shadow').sort((a, b) => b.pct - a.pct)[0] || patterns[0] || null;

    const goal = Math.min(CEILING, index + GAIN);
    const projection = goal - index >= MIN_HEADROOM ? { now: index, goal, weeks: trajectory(index, goal) } : null;

    return {
        ready: true,
        dirCode: track,
        dirName: dir ? dir.name : track,
        dirSub: dir ? dir.sub : '',
        index,
        patterns,
        blocker,
        patternTotal: content.patterns.length,
        directions: content.directions.map((d) => ({
            code: d.code,
            name: d.name,
            score: d.code === track ? index : null,
        })),
        projection,
    };
}

/** React entry point: loads the content payload, then scores the run. */
export function usePaywallProfile(userProfile: UserProfile, pages: QuizPage[] | undefined): PaywallProfile {
    const { content } = useAppContent();

    return React.useMemo(() => {
        if (!content || !pages || !pages.length) return EMPTY;

        return computePaywallProfile(userProfile, pages, content);
    }, [content, pages, userProfile]);
}

/** Zone colour of the index, so screens do not each re-derive it. */
export const indexColor = (index: number | null): string => scoreColor(index).hex;
